import React, { useState, useCallback, useEffect } from 'react';
import { Paper, Stack, Typography, Box, Tooltip, ButtonBase } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { useGetAverageProcedureSpendQuery } from '../../api/procedureSlice';
import { formatCurrency } from '../../utils/formatNumbers';
import { theme } from '../../theme';

// Renders nothing — fetches one procedure's spend data and lifts it to the parent.
function ProcedureSpendFetcher({ procedureId, onLoaded }) {
    const { data, isSuccess } = useGetAverageProcedureSpendQuery({ procedureId });
    useEffect(() => {
        if (isSuccess) onLoaded(procedureId, data ?? []);
    }, [data, isSuccess, procedureId, onLoaded]);
    return null;
}

function MultiProcedureSpendChart({ selectedProcedures, onViewDetails }) {
    const [lineDataMap, setLineDataMap] = useState({});

    const handleLoaded = useCallback((procedureId, data) => {
        setLineDataMap(prev => prev[procedureId] === data ? prev : { ...prev, [procedureId]: data });
    }, []);

    const palette = Object.values(theme.palette.graphColors || {});

    const lineData = selectedProcedures.map((p, i) => ({
        id: p.name,
        procedureId: p.id,
        color: palette[i % palette.length],
        data: (lineDataMap[p.id] ?? []).map(d => ({
            x: d.period,
            y: parseFloat(d.average_case_spend),
        }))
    }));

    const allLoaded = selectedProcedures.every(p => lineDataMap[p.id] !== undefined);
    const boxHeight = 280;

    return (
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            { selectedProcedures.map(p => (
                <ProcedureSpendFetcher key={p.id} procedureId={p.id} onLoaded={handleLoaded} />
            ))}
            <Stack direction='row' justifyContent='space-between' alignItems='flex-start' mb={2}>
                <Stack>
                    <Typography variant='h6' fontWeight={700}>Average Case Cost Over Time</Typography>
                    <Typography variant='body2' color='text.secondary'>Click a procedure in the legend to view its full analysis</Typography>
                </Stack>
            </Stack>
            { !allLoaded && <Typography variant='body2'>Loading...</Typography> }
            { allLoaded && (
                <Box sx={{ height: boxHeight, display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <ResponsiveLine
                            data={lineData}
                            margin={{ top: 10, right: 20, bottom: 70, left: 80 }}
                            colors={line => line.color}
                            xScale={{ type: 'point' }}
                            yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                            axisBottom={{
                                tickRotation: -45,
                                legend: 'Month',
                                legendOffset: 48,
                                legendPosition: 'middle',
                                format: v => {
                                    const [year, month] = String(v).split('-');
                                    if (!year || !month) return v;
                                    return new Date(year, month - 1).toLocaleString('en-US', { month: 'short', year: '2-digit' });
                                },
                            }}
                            axisLeft={{
                                legend: 'Avg Case Cost',
                                legendOffset: -68,
                                legendPosition: 'middle',
                                format: v => formatCurrency(v, 0),
                            }}
                            lineWidth={2}
                            enablePoints={true}
                            pointSize={6}
                            useMesh={true}
                            legends={[]}
                            tooltip={({ point }) => {
                                const [year, month] = String(point.data.x).split('-');
                                const label = new Date(year, month - 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
                                return (
                                    <Box sx={{ backgroundColor: '#fff', padding: 1, borderRadius: 1, boxShadow: 3 }}>
                                        <Typography variant='body2'><b>{point.serieId}</b></Typography>
                                        <Typography variant='body2'>{label}: <b>{formatCurrency(point.data.y)}</b></Typography>
                                    </Box>
                                );
                            }}
                        />
                    </Box>
                    <Box sx={{ width: 160, overflow: 'auto', pr: 1 }}>
                        <Stack spacing={1}>
                            {selectedProcedures.map((p, i) => (
                                <Tooltip key={p.id} title={p.name} placement='left'>
                                    <ButtonBase
                                        onClick={() => onViewDetails(p.id)}
                                        aria-label={`View ${p.name}`}
                                        sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-start', cursor: 'pointer', width: '100%', textAlign: 'left', px: 0.5, minWidth: 0 }}
                                    >
                                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: palette[i % palette.length], flex: '0 0 12px' }} />
                                        <Typography variant='body2' noWrap sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</Typography>
                                    </ButtonBase>
                                </Tooltip>
                            ))}
                        </Stack>
                    </Box>
                </Box>
            )}
        </Paper>
    );
}

export default MultiProcedureSpendChart;
